import { MDSIcon, MDSIconColor, MDSIconKind } from "@/mds/components/icon";
import { MDSTooltip, MDSTooltipTrigger } from "@/mds/components/tooltip";
import {
  StyledWrapper,
  StyledIconWrapper,
  StyledContentContainer,
  StyledTitleRow,
  StyledTitle,
  StyledExtraText,
  StyledSubtitle,
  StyledRightAddon,
} from "./menu-item.style";
import {
  MDSMenuItemIconVariant,
  MDSMenuItemProps,
  MDSMenuItemSelectionVariant,
  MDSMenuItemVariant,
} from "./menu-item.types";

/**
 * MDSMenuItem
 * This is the base menu item that implements the Design System styling and can be used independently
 * This component needs to be wrapped in either ListBoxItem or MenuItem to be used in MDSSelectMenu or MDSActionMenu
 */
export const MDSMenuItem = (props: MDSMenuItemProps) => {
  const hasRightAddon = props.selectionVariant || props.rightAddon;
  const hasRightAddonSelectionVariant =
    !!props.selectionVariant && props.selectionVariant !== MDSMenuItemSelectionVariant.Hover;
  const isInert = props.isInert ?? false;

  const menuItemContent = (
    <StyledWrapper
      {...props}
      id={props.id}
      data-mds-name="mds-menu-item"
      onClick={isInert ? undefined : props.onClick}
      aria-label={props.label}
      $variant={props.variant ?? MDSMenuItemVariant.TwoLine}
      $isInert={isInert}
      $isHovered={
        !isInert && props.selectionVariant === MDSMenuItemSelectionVariant.Hover
          ? (props.isSelected ?? false)
          : false
      }
    >
      {props.icon && (
        <StyledIconWrapper
          $iconVariant={props.iconVariant ?? MDSMenuItemIconVariant.Icon}
          $hasSubtitle={!!props.subtitle}
        >
          {typeof props.icon === "function" ? (
            props.icon({ size: 11, boxSize: 16, color: MDSIconColor.Primary })
          ) : (
            <MDSIcon kind={props.icon} size={11} />
          )}
        </StyledIconWrapper>
      )}

      <StyledContentContainer>
        <StyledTitleRow>
          <StyledTitle>
            {props.label}
            {props.extraText && <StyledExtraText>{` - ${props.extraText}`}</StyledExtraText>}
          </StyledTitle>
          {props.isShared && (
            <MDSIcon kind={MDSIconKind.Shared} color={MDSIconColor.Secondary} size={10} />
          )}
        </StyledTitleRow>
        {props.subtitle && <StyledSubtitle>{props.subtitle}</StyledSubtitle>}
      </StyledContentContainer>

      {hasRightAddon && (
        <StyledRightAddon>
          {props.selectionVariant === MDSMenuItemSelectionVariant.Checkmark && props.isSelected && (
            <MDSIcon kind={MDSIconKind.Check} color={MDSIconColor.Selected} />
          )}
          {props.selectionVariant === MDSMenuItemSelectionVariant.RadioButton &&
            (props.isSelected ? (
              <MDSIcon kind={MDSIconKind.CheckCircleSolid} color={MDSIconColor.Selected} />
            ) : (
              <MDSIcon kind={MDSIconKind.Circle} color={MDSIconColor.Secondary} />
            ))}
          {!hasRightAddonSelectionVariant && !!props.rightAddon && props.rightAddon()}
        </StyledRightAddon>
      )}
    </StyledWrapper>
  );

  return (
    <MDSTooltip config={props.tooltipConfig}>
      {props.tooltipConfig ? (
        <MDSTooltipTrigger>{menuItemContent}</MDSTooltipTrigger>
      ) : (
        menuItemContent
      )}
    </MDSTooltip>
  );
};
